import {
  createHttpLink 
} from "@apollo/client"

import createAuthLink from "@/Apollo/links/authLink"
import createErrorLink from "@/Apollo/links/errorLink"
import createGqlCacheLink from "@/Apollo/links/gqlCacheLink"
import createMutationQueueLink from "@/Apollo/links/mutationQueueLink"
import createNetworkErrorNotifyLink from "@/Apollo/links/networkErrorNotifyLink"
import createRetryLink from "@/Apollo/links/retryLink"
import createStoreLink from "@/Apollo/links/storeLink"
import shrinkQuery from "@/util/shrinkQuery"

/**
 * Intercept and shrink URLs from GET queries.
 *
 * Using GET makes it possible to use edge caching in Magento Cloud, but risks
 * exceeding URL limits with default usage of Apollo's http link.
 *
 * `shrinkQuery` encodes the URL in a more efficient way.
 *
 * @param {*} uri
 * @param {*} options
 */
export const customFetchToShrinkQuery = (uri, options) => {
  // TODO: add `ismorphic-fetch` or equivalent to avoid this error
  if (typeof globalThis.fetch !== "function") {
    console.error("This environment does not define `fetch`.")

    return () => {}
  }

  const resource = options.method === "GET" ? shrinkQuery(uri) : uri

  return globalThis.fetch(resource, options)
}

const getLinks = (apiBase) => {
  const authLink = createAuthLink()
  const storeLink = createStoreLink()
  const errorLink = createErrorLink()
  const retryLink = createRetryLink()
  const gqlCacheLink = createGqlCacheLink()
  const mutationQueueLink = createMutationQueueLink()
  const networkErrorNotifyLink = createNetworkErrorNotifyLink()

  // Warning: `useGETForQueries` risks exceeding URL length limits.
  // These limits in practice are typically set at or behind where TLS
  // terminates. For Magento Cloud & Fastly, 8kb is the maximum by default.
  // https://docs.fastly.com/en/guides/resource-limits#request-and-response-limits
  const httpLink = createHttpLink({
    fetch: customFetchToShrinkQuery,
    useGETForQueries: true,
    uri: apiBase,
  })

  // preserve this array order, it's important
  // as the terminating link, `httpLink` must be last
  const links = new Map()
    .set("MUTATION_QUEUE", mutationQueueLink)
    // 必须在 RETRY 上游，只在重试全部失败后才提示用户刷新
    .set("NETWORK_ERROR_NOTIFY", networkErrorNotifyLink)
    .set("RETRY", retryLink)
    .set("AUTH", authLink)
    .set("GQL_CACHE", gqlCacheLink)
    .set("STORE", storeLink)
    .set("ERROR", errorLink)
    .set("HTTP", httpLink)

  return links
}

export default getLinks
